import * as React from 'react';
import {View} from 'react-native';
import {flow, join, slice, toInteger} from 'lodash/fp';

import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {CoachInfoCard} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {styles} from './PromotionReportContent.style';

interface PromotionReportContentProps {
  tt: (key: string, defaultValue: string) => string;
  recordData: {
    newRank?: {description: string};
    previousRank?: {description: string};
    promotedOn?: string;
  };
}

const PromotionReportContent: React.FC<PromotionReportContentProps> = ({
  tt = (key: string, defaultValue: string) => defaultValue, // Default value for `tt`
  recordData = {}, // Default value for `recordData`
}: PromotionReportContentProps) => {
  const {
    newRank: {description: rankDescription} = {},
    previousRank: {description: previousRankDescription} = {},
    promotedOn = '', // Default to empty string if `promotedOn` is undefined
  } = recordData;

  // The `promotedOn` date is represented in `MMYYYY` format,
  // so the first two digits indicate the month number
  // and the last four digits the year
  const promotedOnMonth = flow(slice(0, 2), join(''), toInteger)(promotedOn);
  const promotedOnYear = flow(slice(2, 6), join(''), toInteger)(promotedOn);
  // Subtract 1 month, since the number of months in the response starts from 1, not 0
  const promotedOnDate = new Date(promotedOnYear, promotedOnMonth - 1);

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <Gutter>
          <Text variant="heading">{tt('heading', 'Rank Up')}</Text>
        </Gutter>
      </View>
      <Gutter>
        <ListItem
          style={{base: styles.listItem}}
          label={<Text>{tt('label.newHAR', 'New HRA')}</Text>}
          suffix={<Text variant="button">{rankDescription}</Text>}
          testID={CoachInfoCard.newHARContent}
        />
        <ListItem
          style={{base: styles.listItem}}
          label={<Text>{tt('label.previousHAR', 'Previous HRA')}</Text>}
          suffix={<Text variant="button">{previousRankDescription}</Text>}
          testID={CoachInfoCard.previousHARContent}
        />
        <ListItem
          style={{base: styles.listItem}}
          label={<Text>{tt('label.rankUpFor', 'Rank Up For')}</Text>}
          suffix={
            <Text variant="button">
              {getDateUtilDefaultInstance().formatDate(
                promotedOnDate,
                'MMMM yyyy',
              )}
            </Text>
          }
          testID={CoachInfoCard.rankUpForContent}
        />
      </Gutter>
    </View>
  );
};

export default withTranslation('reportCardContent.promotion')(
  PromotionReportContent,
);
